import moment from 'moment';
import momentTz from 'moment-timezone';

import { BTSessionStatus, CT_TIMEZONE_AREA, Roles } from '../constants';
import { areUserRolesIn } from '../helpers';
import { CQAReviewStatus } from '../components/layout/qa/constants';
import { TPGoalDataCollectionMethod } from '../components/layout/treatment-plan/goals/common/constants/general';
import { DurationChartPlotMethod } from './constants';

import type {
  IDataDashboardGoalOption,
  IDataDashboardOptionSelection,
  ISessionMinified,
} from '../utils/types';
interface TrialAverageResult {
  value: number;
  label: string;
}

export const formatDataDashboardDate = (date: string) =>
  momentTz.utc(date).tz(CT_TIMEZONE_AREA).format('MM/DD/YY');

export const getInitialDashboardOptionSelection = (
  goals: IDataDashboardGoalOption[],
): IDataDashboardOptionSelection => {
  if (goals.length !== 1) return { goalId: null, targetIds: [] };
  return {
    goalId: goals[0].id,
    targetIds: goals[0].targets.length === 1 ? [goals[0].targets[0].id] : [],
  };
};

// Filters data for dashboard session cards
export const getSessionCardData = (
  sessions: ISessionMinified[] = [],
  userRoles: Roles[],
  computeCQAReviewSection: boolean,
  showActionRequiredVBTSection: boolean,
) => {
  if (!sessions.length || !userRoles) return [[], [], []];
  // Sort sessions
  const sortedRecentSession = sessions.slice().sort((a: ISessionMinified, b: ISessionMinified) => {
    if (moment(a.date_start).isAfter(moment(b.date_start), 'days')) {
      return -1;
    } else if (moment(a.date_start).isBefore(moment(b.date_start), 'days')) {
      return 1;
    }
    return 0;
  });

  // Filter data for CQA reviewed section
  let cqaReviewedSessions: ISessionMinified[] = [];
  const cqaReviewSessionIdMap = new Map();

  if (computeCQAReviewSection) {
    cqaReviewedSessions = sortedRecentSession.filter(clinicalQAReviewedSessionsCriteria);
    cqaReviewedSessions.forEach((item) => {
      cqaReviewSessionIdMap.set(Number(item.id), item.id);
    });
  }

  // Filter data for recent session section
  const openSessions = sortedRecentSession.filter(openSessionCriteria);

  const closedSessions = sortedRecentSession.filter((session: ISessionMinified) =>
    closedSessionCriteria(session, userRoles, cqaReviewSessionIdMap),
  );
  const recentSessions = [...openSessions, ...closedSessions];

  // Filter data for action required VBT section
  let actionRequiredVBTSessions: ISessionMinified[] = [];
  if (showActionRequiredVBTSection) {
    actionRequiredVBTSessions = sortedRecentSession.filter(actionRequiredSessionsVBTCriteria);
  }

  return [recentSessions, cqaReviewedSessions, actionRequiredVBTSessions];
};

// ********* Sessions filtering criteria ********** //
const openSessionCriteria = (session: ISessionMinified) => {
  return session.status === BTSessionStatus.Started;
};

const closedSessionCriteria = (
  session: ISessionMinified,
  userRoles: Roles[],
  cqaSessionIdMap: Map<number, number>,
) =>
  !cqaSessionIdMap.has(Number(session.id)) && // Should not be shown, if already shown in CQA section
  (session.status === BTSessionStatus.AwaitingReview ||
    session.status === BTSessionStatus.Completed ||
    (session.status === BTSessionStatus.AwaitingGuardianSignature &&
      areUserRolesIn(userRoles, [Roles.Prbt])));

const actionRequiredSessionsVBTCriteria = (session: ISessionMinified) =>
  session.status === BTSessionStatus.Started ||
  session?.status === BTSessionStatus.EditsRequested ||
  (session?.status === BTSessionStatus.Completed &&
    session?.clinical_qa_review?.status !== CQAReviewStatus.Acknowledged);

const clinicalQAReviewedSessionsCriteria = (session: ISessionMinified) =>
  Boolean(
    // Session note must be graded by QA
    session?.clinical_qa_review?.status === CQAReviewStatus.AwaitingAcknowledgement ||
    session?.clinical_qa_review?.status === CQAReviewStatus.Acknowledged,
  ) &&
  (Boolean(session.status === BTSessionStatus.EditsRequested) || // can be in edit requested status
    session?.clinical_qa_review?.status !== CQAReviewStatus.Acknowledged); // or if not acknowledged(signed)

// ********* Sessions filtering criteria ********** //

export const calculateTrialAverage = (trials: any[]): TrialAverageResult => {
  if (!trials.length) return { value: 0, label: '' };

  const dcm = trials[0].type;

  if (dcm === TPGoalDataCollectionMethod.PERCENTAGE) {
    const totalAttempts = trials.reduce((sum, trial) => sum + trial.attempts, 0);
    const totalSuccess = trials.reduce((sum, trial) => sum + trial.success, 0);
    const value = totalAttempts > 0 ? Number(((totalSuccess / totalAttempts) * 100).toFixed(1)) : 0;
    return {
      value,
      label: 'Average success percentage',
    };
  }

  if (dcm === TPGoalDataCollectionMethod.INSTANCES) {
    // Check if duration exists in any of the trials
    if (trials.some((trial) => trial.durations?.length > 0)) {
      const totalDuration = trials.reduce((sum, trial) => {
        const trialDurationSum = trial.durations.reduce((a: number, b: number) => a + b, 0);
        return sum + trialDurationSum;
      }, 0);
      const totalInstances = trials.reduce((sum, trial) => sum + trial.instances, 0);
      const value = totalInstances > 0 ? Number((totalDuration / totalInstances).toFixed(1)) : 0;
      return {
        value,
        label: 'Average duration of instances',
      };
    } else {
      const totalInstances = trials.reduce((sum, trial) => sum + trial.instances, 0);
      const value = Number((totalInstances / trials.length).toFixed(1));
      return {
        value,
        label: 'Average of Instances',
      };
    }
  }

  if (dcm === TPGoalDataCollectionMethod.RATE) {
    const totalRate = trials.reduce((sum, trial) => sum + trial.rate, 0);
    const value = totalRate > 0 ? parseFloat((totalRate / trials.length).toFixed(2)) : 0;
    return {
      value,
      label: 'Average rate',
    };
  }

  if (dcm === TPGoalDataCollectionMethod.DURATION) {
    const averageDuration = (
      trials.reduce((sum, trial) => sum + (trial.durationEventAverage ?? 0), 0) / trials.length
    ).toFixed(1);
    return {
      value: Number(averageDuration),
      label: 'Average duration',
    };
  }

  return { value: 0, label: '' };
};

export const calculateDurationTrialSummaryData = (trials: any[]) => {
  if (!trials.length) return { average: 0, label: '', total: 0 };

  const averageDuration = (
    trials.reduce((sum, trial) => sum + trial.durationEventAverage, 0) / trials.length
  ).toFixed(2);

  const totalDuration = trials.reduce((sum, trial) => sum + trial.durationEventTotal, 0).toFixed(2);

  return { average: averageDuration, label: 'Duration summary', total: totalDuration };
};

export const getDataKeyByDCM = (
  dcm: string,
  durationChartPlotMethod: DurationChartPlotMethod = DurationChartPlotMethod.AVERAGE,
) => {
  switch (dcm) {
    case TPGoalDataCollectionMethod.DURATION:
      return durationChartPlotMethod === DurationChartPlotMethod.AVERAGE
        ? 'durationEventAverage'
        : 'durationEventTotal';
    case TPGoalDataCollectionMethod.INSTANCES:
      return 'instances';
    case TPGoalDataCollectionMethod.PERCENTAGE:
      return 'percentage';
    case TPGoalDataCollectionMethod.RATE:
      return 'rate';
    default:
      return 'instances';
  }
};

export const getChartLabelByDCM = (
  dcm: string,
  durationChartPlotMethod: DurationChartPlotMethod = DurationChartPlotMethod.AVERAGE,
) => {
  switch (dcm) {
    case TPGoalDataCollectionMethod.DURATION:
      return durationChartPlotMethod === DurationChartPlotMethod.AVERAGE
        ? 'Avg. Duration'
        : 'Total Duration';
    case TPGoalDataCollectionMethod.INSTANCES:
      return 'Instances';
    case TPGoalDataCollectionMethod.PERCENTAGE:
      return 'Percentage';
    case TPGoalDataCollectionMethod.RATE:
      return 'Rate';
    default:
      return 'Trend';
  }
};

export function getDataDashboardMeasurementUnitLabel(goal: IDataDashboardGoalOption) {
  const units = goal.measurement_units;
  if (!units) return '';
  if (goal.data_collection_method === TPGoalDataCollectionMethod.RATE && 'interval_size' in units) {
    return ` per ${units.interval_size} ${
      units.interval_size === 1 ? units.interval_unit.replace(/s$/, '') : units.interval_unit
    }`;
  }
  if (goal.data_collection_method === TPGoalDataCollectionMethod.DURATION && 'duration_unit' in units) {
    return ` in ${units.duration_unit.toLowerCase()}s`;
  }
  if (goal.data_collection_method === TPGoalDataCollectionMethod.PERCENTAGE && 'total_attempts' in units) {
    return ` from ${units.total_attempts} attempts`;
  }
  return '';
}
